/**
 * The ban list, and a way to ask about one address.
 *
 * Two things on one page because they are the two halves of one job: "who is on this address?"
 * is the question that ends in a ban, and the list is where the ban is undone. The lookup box
 * takes `?ip=` so a user's Network tab can send you here with the address already asked.
 *
 * Nothing on this page can lock the panel: bans are enforced by the app's API only.
 */

import { useEffect, useMemo, useState } from 'react'
import { Link as RouterLink, useSearchParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  InputAdornment,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { Add, Block, LockOpen, Search } from '@mui/icons-material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { PageShell } from '@components/common/PageShell'
import { DataTable, type DataTableColumn } from '@components/common/DataTable'
import { ConfirmDialog } from '@components/common/ConfirmDialog'
import { ipApi } from '@api/endpoints/ip'
import type { IpBanDto, IpLookupDto } from '@api/types/ip'
import { IpBanDialog } from '../components/ip/IpBanDialog'
import { describeBanTerm } from '../components/ip/ipFormatting'
import { IP_BANS_KEY } from '../components/users/userQueryKeys'
import { errorMessage, formatDate } from '../components/users/userFormatting'

export default function AdminIpBansPage() {
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()

  // ---- lookup ----
  const askedIp = searchParams.get('ip') ?? ''
  const [ipInput, setIpInput] = useState(askedIp)
  useEffect(() => setIpInput(askedIp), [askedIp])

  const lookup = useQuery({
    queryKey: ['ip-lookup', askedIp],
    queryFn: () => ipApi.lookup(askedIp),
    enabled: askedIp.length > 0,
    retry: false,
  })

  const ask = () => {
    const next = ipInput.trim()
    setSearchParams(next ? { ip: next } : {}, { replace: true })
  }

  // ---- bans ----
  const [includeInactive, setIncludeInactive] = useState(false)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(25)
  const [banOpen, setBanOpen] = useState(false)
  const [banPrefill, setBanPrefill] = useState('')
  const [liftTarget, setLiftTarget] = useState<IpBanDto | null>(null)

  const bans = useQuery({
    queryKey: [...IP_BANS_KEY, includeInactive],
    queryFn: () => ipApi.listBans(includeInactive),
  })

  const lift = useMutation({
    mutationFn: (id: string) => ipApi.liftBan(id),
    onSuccess: () => {
      setLiftTarget(null)
      queryClient.invalidateQueries({ queryKey: IP_BANS_KEY })
      queryClient.invalidateQueries({ queryKey: ['ip-lookup'] })
    },
  })

  const openBan = (prefill: string) => {
    setBanPrefill(prefill)
    setBanOpen(true)
  }

  const columns = useMemo<DataTableColumn<IpBanDto>[]>(
    () => [
      {
        key: 'rule',
        header: 'Address / range',
        primary: true,
        render: (ban) => (
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography component="span" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
              {ban.rule}
            </Typography>
            {ban.isRange && <Chip size="small" label="range" variant="outlined" />}
            <Chip
              size="small"
              color={ban.isActive ? 'error' : 'default'}
              label={ban.isActive ? `Active · ${describeBanTerm(ban)}` : describeBanTerm(ban)}
            />
          </Stack>
        ),
      },
      {
        key: 'reason',
        header: 'Reason',
        render: (ban) => <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{ban.reason}</Typography>,
      },
      {
        key: 'createdAt',
        header: 'Placed',
        hideBelow: 'md',
        render: (ban) => (
          <Typography variant="body2">
            {formatDate(ban.createdAt)}
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              by {ban.createdByName || 'unknown'}
            </Typography>
          </Typography>
        ),
      },
      {
        key: 'subject',
        header: 'From account',
        hideBelow: 'lg',
        render: (ban) =>
          ban.subjectUserId ? (
            <Chip
              size="small"
              variant="outlined"
              clickable
              component={RouterLink}
              to={`/users/${ban.subjectUserId}?tab=network`}
              label={ban.subjectName || ban.subjectUserId}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          ),
      },
    ],
    [],
  )

  return (
    <PageShell
      title="IP bans"
      subtitle="Addresses the app refuses. The panel is never blocked, and changes take about thirty seconds to reach the app."
      actions={
        <Button variant="contained" color="error" startIcon={<Add />} onClick={() => openBan('')}>
          Ban an address
        </Button>
      }
    >
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
          <TextField
            label="Look up an address"
            value={ipInput}
            onChange={(e) => setIpInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') ask()
            }}
            placeholder="203.0.113.9"
            size="small"
            fullWidth
            inputProps={{ spellCheck: false, autoCapitalize: 'off', style: { fontFamily: 'monospace' } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Button variant="outlined" onClick={ask} sx={{ flexShrink: 0 }}>
            Look up
          </Button>
        </Stack>

        {askedIp && <LookupResult query={lookup} onBan={() => openBan(askedIp)} />}
      </Paper>

      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
        <FormControlLabel
          control={<Switch checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} />}
          label="Show lifted and expired"
        />
      </Stack>

      <DataTable<IpBanDto>
        rows={bans.data ?? []}
        columns={columns}
        getRowKey={(ban) => ban.id}
        loading={bans.isLoading}
        error={bans.error ? errorMessage(bans.error, 'Failed to load bans') : null}
        onRetry={() => bans.refetch()}
        emptyMessage={includeInactive ? 'No bans have ever been placed.' : 'No active bans.'}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        getRowActions={(ban) =>
          ban.isActive
            ? [{ key: 'lift', label: 'Lift', icon: <LockOpen />, onClick: () => setLiftTarget(ban) }]
            : []
        }
      />

      <IpBanDialog open={banOpen} onClose={() => setBanOpen(false)} initialRule={banPrefill} />

      <ConfirmDialog
        open={liftTarget !== null}
        title="Lift this ban?"
        message={
          liftTarget
            ? `${liftTarget.rule} will be allowed back in within about thirty seconds. The ban stays in the history as lifted.`
            : ''
        }
        confirmLabel="Lift"
        loading={lift.isPending}
        onConfirm={() => liftTarget && lift.mutate(liftTarget.id)}
        onClose={() => setLiftTarget(null)}
      >
        {lift.error && <Alert severity="error" sx={{ mt: 1 }}>{errorMessage(lift.error, 'Failed to lift the ban')}</Alert>}
      </ConfirmDialog>
    </PageShell>
  )
}

function LookupResult({
  query,
  onBan,
}: {
  query: { data?: IpLookupDto; isLoading: boolean; error: unknown }
  onBan: () => void
}) {
  if (query.isLoading) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        Looking up…
      </Typography>
    )
  }
  if (query.error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {errorMessage(query.error, 'Lookup failed')}
      </Alert>
    )
  }
  const result = query.data
  if (!result) return null

  return (
    <Box sx={{ mt: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        <Typography sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{result.ipAddress}</Typography>
        {result.activeBan ? (
          <Chip size="small" color="error" icon={<Block />} label={`Banned ${describeBanTerm(result.activeBan)}`} />
        ) : (
          <Button size="small" color="error" startIcon={<Block />} onClick={onBan}>
            Ban this address
          </Button>
        )}
      </Stack>
      {/* The reason used to live only in a hover tooltip on the chip; a phone never saw it. */}
      {result.activeBan && (
        <Typography variant="body2" sx={{ mb: 1, overflowWrap: 'anywhere' }}>
          <Box component="span" sx={{ fontFamily: 'monospace' }}>
            {result.activeBan.rule}
          </Box>
          {': '}
          {result.activeBan.reason || 'No reason recorded.'}
        </Typography>
      )}

      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        {result.accounts.length === 0
          ? 'No accounts have been seen on this address.'
          : `${result.accounts.length} account${result.accounts.length === 1 ? '' : 's'} seen here`}
      </Typography>
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
        {result.accounts.map((account) => (
          <Chip
            key={account.userId}
            size="small"
            variant="outlined"
            color={account.isActive ? 'default' : 'warning'}
            clickable
            component={RouterLink}
            to={`/users/${account.userId}?tab=network`}
            label={`${account.displayName || account.email} · ${formatDate(account.lastSeenAt)}`}
            title={account.email}
          />
        ))}
      </Stack>

      {result.bans.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {result.bans.length} ban{result.bans.length === 1 ? '' : 's'} on record {result.bans.length === 1 ? 'covers' : 'cover'} this address
          {result.bans.some((b) => !b.isActive) ? ', including lifted or expired ones' : ''}.
        </Typography>
      )}
    </Box>
  )
}
